import React, { useState, useEffect, useMemo, useDeferredValue } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import {
  EllipsisHorizontalIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  MapPinIcon,
  IdentificationIcon,
  EnvelopeIcon,
  LinkIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { Player } from '../../types/player';
import { playersAPI, ageGroupsAPI, exportsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import { absenceBadgeClass, describeAbsence } from '../../utils/absence';
import PlayerImportModal from './PlayerImportModal';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin, isStaff } from '../../types/auth';
import PlayerModal from './PlayerModal';
import BulkInviteModal from './BulkInviteModal';
import PlayerDetailModal from './PlayerDetailModal';
import { usePageTitle } from '../../hooks/usePageTitle';
import IfaLinkModal from './IfaLinkModal';
import DeleteConfirmModal from '../ui/DeleteConfirmModal';
import HelpHint from '../help/HelpHint';
import { getMedicalState, medicalBadge, MedicalState, useSportDoctorWarningDays } from '../../utils/playerMedical';
import { matchesSearch } from '../../utils/search';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useBatchedList } from '../../hooks/useBatchedList';
import LazyLoadTrigger from '../ui/LazyLoadTrigger';
import SportDoctorCheckModal from './SportDoctorCheckModal';

/** Kis jelvények a név mellett: hiányzás és egészségügyi tudnivaló. */
const PlayerFlags: React.FC<{ player: Player }> = ({ player }) => (
  <>
    {player.currentAbsence && (
      <span
        className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${absenceBadgeClass(player.currentAbsence.type)}`}
        title="Betegség / sérülés bejelentve"
      >
        {describeAbsence(player.currentAbsence)}
      </span>
    )}
    {player.medicalNotes && (
      <span title={`Egészségügyi tudnivaló: ${player.medicalNotes}`} className="inline-flex text-amber-600">
        <ExclamationTriangleIcon className="h-4 w-4" />
      </span>
    )}
  </>
);

/** Gyorsszűrők a lista fölött: a nagy létszámnál ezekkel lehet célzottan dolgozni. */
type QuickFilter = 'all' | 'expired' | 'expiring' | 'missing' | 'noIfa' | 'absent';

const QUICK_FILTERS: { id: QuickFilter; label: string; tone: string }[] = [
  { id: 'all', label: 'Mind', tone: 'bg-gray-800 text-white' },
  { id: 'expired', label: 'Lejárt sportorvosi', tone: 'bg-red-600 text-white' },
  { id: 'missing', label: 'Nincs sportorvosi', tone: 'bg-red-600 text-white' },
  { id: 'expiring', label: 'Hamarosan lejár', tone: 'bg-amber-500 text-white' },
  { id: 'noIfa', label: 'Nincs IFA-kód', tone: 'bg-amber-500 text-white' },
  { id: 'absent', label: 'Beteg / sérült', tone: 'bg-amber-500 text-white' },
];

type SortField = 'name' | 'ageGroup' | 'birth' | 'medical';

const SortHeader: React.FC<{
  label: string;
  field: SortField;
  sort: { field: SortField; dir: 'asc' | 'desc' };
  onSort: (field: SortField) => void;
}> = ({ label, field, sort, onSort }) => {
  const active = sort.field === field;
  const Icon = active && sort.dir === 'desc' ? ChevronDownIcon : ChevronUpIcon;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-900 ${active ? 'text-gray-900' : ''}`}
    >
      {label}
      <Icon className={`h-3.5 w-3.5 ${active ? '' : 'opacity-30'}`} />
    </button>
  );
};

/** Ennyi sor jelenik meg egyszerre; görgetéskor jön a következő adag. */
const PAGE_SIZE = 60;

const PlayersList: React.FC = () => {
  usePageTitle('Játékosok');
  const { id: playerIdFromPath } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('');
  const [isMedicalModalOpen, setIsMedicalModalOpen] = useState(false);
  const sportDoctorWarningDays = useSportDoctorWarningDays();
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const [sort, setSort] = useState<{ field: SortField; dir: 'asc' | 'desc' }>({ field: 'name', dir: 'asc' });
  // Gépelés közben a 800 soros szűrés ne fogja meg a beviteli mezőt
  const deferredSearch = useDeferredValue(searchTerm);
  const isMobile = useIsMobile();
  const [ifaLinkPlayer, setIfaLinkPlayer] = useState<Player | null>(null);
  const [isBulkInviteOpen, setIsBulkInviteOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { user } = useAuth();
  const { showToast } = useToast();

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportsAPI.players(showInactive);
    } catch (err) {
      showToast(getErrorMessage(err, 'Az exportálás nem sikerült.'), 'error');
    } finally {
      setExporting(false);
    }
  };

  /** Hiányzás bejelentése után a lista jelvényei és a nyitott adatlap is frissüljön. */
  const handleAbsenceChanged = async () => {
    const data = await fetchPlayers();
    setPlayers(data);
    setSelectedPlayer((prev) => (prev ? data.find((p) => p.id === prev.id) ?? prev : prev));
  };

  const [ageGroups, setAgeGroups] = useState<string[]>([]);

  useEffect(() => {
    loadAgeGroups();
  }, []);

  useEffect(() => {
    loadPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const fetchPlayers = () => playersAPI.getAll(showInactive);

  const loadAgeGroups = async () => {
    try {
      const data = await ageGroupsAPI.getForSelect();
      setAgeGroups(data.map(ag => ag.name));
    } catch (err) {
      console.error('Failed to load age groups:', err);
    }
  };

  const loadPlayers = async () => {
    try {
      setLoading(true);
      const data = await fetchPlayers();
      setPlayers(data);
    } catch (err) {
      setError('Hiba történt a játékosok betöltésekor');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Keresés és korosztály - erre számoljuk a gyorsszűrők darabszámát is
  const baseFiltered = useMemo(
    () =>
      players.filter(
        (player) =>
          matchesSearch(deferredSearch, player.name, player.taj, player.ifaCode) &&
          (!selectedAgeGroup ||
            (player.ageGroups && player.ageGroups.includes(selectedAgeGroup)) ||
            player.ageGroup === selectedAgeGroup)
      ),
    [players, deferredSearch, selectedAgeGroup]
  );

  const medicalStateOf = useMemo(() => {
    const map = new Map<number, MedicalState>();
    players.forEach((p) => map.set(p.id, getMedicalState(p, sportDoctorWarningDays)));
    return (p: Player) => map.get(p.id) ?? 'missing';
  }, [players, sportDoctorWarningDays]);

  const matchesQuickFilter = (p: Player, filter: QuickFilter): boolean => {
    switch (filter) {
      case 'expired':
      case 'expiring':
      case 'missing':
        return !p.isInactive && medicalStateOf(p) === filter;
      case 'noIfa':
        return !p.ifaCode;
      case 'absent':
        return !!p.currentAbsence;
      default:
        return true;
    }
  };

  const quickFilterCounts = useMemo(() => {
    const counts = {} as Record<QuickFilter, number>;
    QUICK_FILTERS.forEach((f) => {
      counts[f.id] = baseFiltered.filter((p) => matchesQuickFilter(p, f.id)).length;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseFiltered, medicalStateOf]);

  const filteredPlayers = useMemo(() => {
    const list = baseFiltered.filter((p) => matchesQuickFilter(p, quickFilter));
    const medicalTime = (p: Player) => (p.sportDoctorValidity ? new Date(p.sportDoctorValidity).getTime() : -Infinity);
    const ageGroupOf = (p: Player) => (p.ageGroups?.length ? p.ageGroups.join(', ') : p.ageGroup) ?? '';
    const compare: Record<SortField, (a: Player, b: Player) => number> = {
      name: (a, b) => a.name.localeCompare(b.name, 'hu'),
      ageGroup: (a, b) => ageGroupOf(a).localeCompare(ageGroupOf(b), 'hu') || a.name.localeCompare(b.name, 'hu'),
      birth: (a, b) => new Date(a.birth).getTime() - new Date(b.birth).getTime(),
      medical: (a, b) => medicalTime(a) - medicalTime(b),
    };
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => sign * compare[sort.field](a, b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseFiltered, quickFilter, sort, medicalStateOf]);

  const { visible: visiblePlayers, hasMore, remaining, loadMore } = useBatchedList(filteredPlayers, PAGE_SIZE);

  const toggleSort = (field: SortField) =>
    setSort((prev) => (prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }));

  // Mélylink (/players/12): megnyitjuk a játékos adatlapját. A cím nyitva tartás
  // alatt megmarad (megosztható), bezáráskor áll vissza a listára.
  useEffect(() => {
    if (!playerIdFromPath || players.length === 0) {
      return;
    }

    const target = players.find((p) => p.id === parseInt(playerIdFromPath, 10));
    if (target) {
      setSelectedPlayer(target);
      setIsDetailModalOpen(true);
    } else {
      navigate('/players', { replace: true });
    }
  }, [playerIdFromPath, players, navigate]);

  const handleEditPlayer = (player: Player) => {
    setSelectedPlayer(player);
    setIsDetailModalOpen(false);
    navigate('/players', { replace: true });
    setIsPlayerModalOpen(true);
  };

  // Kártyára/sorra kattintva előbb a nem szerkeszthető adatlap nyílik meg
  const handleViewPlayer = (player: Player) => {
    setSelectedPlayer(player);
    setIsDetailModalOpen(true);
    navigate(`/players/${player.id}`, { replace: true });
  };

  const handleDeletePlayer = (player: Player) => {
    setSelectedPlayer(player);
    setIsDeleteModalOpen(true);
  };

  const handlePlayerSaved = () => {
    loadPlayers();
    setIsPlayerModalOpen(false);
    setIsDetailModalOpen(false);
    setSelectedPlayer(null);
  };

  const handlePlayerDeleted = async () => {
    if (selectedPlayer) {
      try {
        await playersAPI.delete(selectedPlayer.id);
        loadPlayers();
        setIsDeleteModalOpen(false);
        setSelectedPlayer(null);
      } catch (err) {
        setError('Hiba történt a játékos törlésekor');
        console.error(err);
      }
    }
  };

  const canModifyPlayers = isStaff(user);

  const handleToggleActive = async () => {
    if (!selectedPlayer) return;

    let reason: string | undefined;
    if (!selectedPlayer.isInactive) {
      const answer = window.prompt(
        `${selectedPlayer.name} inaktiválása. Az adatai megmaradnak, de alapból sehol nem jelenik meg, ` +
          'nem kerül az eseményekre és nem kap tagdíjat.\n\nIndoklás (opcionális, pl. "Eligazolt"):',
        ''
      );
      if (answer === null) return;
      reason = answer;
    } else if (!window.confirm(`Visszaaktiválod ${selectedPlayer.name} játékost?`)) {
      return;
    }

    try {
      const updated = await playersAPI.setActive(selectedPlayer.id, !!selectedPlayer.isInactive, reason);
      setSelectedPlayer(updated);
      setPlayers(prev =>
        updated.isInactive && !showInactive
          ? prev.filter(p => p.id !== updated.id)
          : prev.map(p => (p.id === updated.id ? { ...p, ...updated } : p))
      );
      if (updated.isInactive && !showInactive) {
        setIsDetailModalOpen(false);
        setSelectedPlayer(null);
      }
    } catch (err) {
      setError('Hiba történt a játékos státuszának módosításakor');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Játékosok</h1>
        </div>
        <div className="flex flex-wrap gap-2 justify-start sm:justify-end">
          {canModifyPlayers && (
            <button
              onClick={() => {
                setSelectedPlayer(null);
                setIsPlayerModalOpen(true);
              }}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Új játékos
            </button>
          )}

          {/* Telefonon a másodlagos műveletek egy menüben, hogy ne foglalják el a fél képernyőt */}
          <Menu as="div" className="relative sm:hidden">
            <Menu.Button className="px-4 py-2 rounded-lg text-sm font-medium border flex items-center gap-2 bg-white text-gray-700 border-gray-300 hover:bg-gray-50">
              <EllipsisHorizontalIcon className="h-5 w-5" />
              Továbbiak
            </Menu.Button>
            <Menu.Items className="absolute right-0 z-20 mt-2 w-60 max-w-[calc(100vw-2rem)] origin-top-right rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
              {isAdmin(user) && (
                <Menu.Item>
                  <button type="button" onClick={() => setIsBulkInviteOpen(true)} className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-gray-700 data-[focus]:bg-gray-50">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" /> Meghívók kiküldése
                  </button>
                </Menu.Item>
              )}
              <Menu.Item>
                <button type="button" onClick={() => setIsMedicalModalOpen(true)} className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-gray-700 data-[focus]:bg-gray-50">
                  <IdentificationIcon className="h-5 w-5 text-gray-400" /> Sportorvosi ellenőrzés
                </button>
              </Menu.Item>
              {canModifyPlayers && (
                <Menu.Item>
                  <button type="button" onClick={() => setIsImportOpen(true)} className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-gray-700 data-[focus]:bg-gray-50">
                    <ArrowUpTrayIcon className="h-5 w-5 text-gray-400" /> Import Excelből
                  </button>
                </Menu.Item>
              )}
              <Menu.Item>
                <button type="button" onClick={handleExport} disabled={exporting} className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-gray-700 data-[focus]:bg-gray-50 disabled:opacity-50">
                  <ArrowDownTrayIcon className="h-5 w-5 text-gray-400" /> {exporting ? 'Exportálás...' : 'Excel export'}
                </button>
              </Menu.Item>
            </Menu.Items>
          </Menu>

          <div className="hidden sm:contents">
          {/* A meghívó végpont admin jogot kér, ezért edzőnek nem mutatjuk */}
          {isAdmin(user) && (
            <button
              type="button"
              onClick={() => setIsBulkInviteOpen(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium border flex items-center gap-2 bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            >
              <EnvelopeIcon className="h-5 w-5" />
              Meghívók kiküldése
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsMedicalModalOpen(true)}
            className="px-4 py-2 rounded-lg text-sm font-medium border flex items-center gap-2 bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
          >
            Sportorvosi ellenőrzés
          </button>

          {canModifyPlayers && (
            <button
              type="button"
              onClick={() => setIsImportOpen(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium border flex items-center gap-2 bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
              Import Excelből
            </button>
          )}

          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 rounded-lg text-sm font-medium border flex items-center gap-2 bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            {exporting ? 'Exportálás...' : 'Excel export'}
          </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div>
            <label htmlFor="search" className="mb-1 flex items-center gap-1.5 text-sm font-medium text-gray-700">
              Keresés
              <HelpHint id="players.search" />
            </label>
            <input
              id="search"
              type="text"
              placeholder="Név, TAJ, IFA kód…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="ageGroup" className="block text-sm font-medium text-gray-700 mb-1">
              Korosztály
            </label>
            <select
              id="ageGroup"
              value={selectedAgeGroup}
              onChange={(e) => setSelectedAgeGroup(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Összes korosztály</option>
              {ageGroups.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </select>
          </div>
        </div>
        {canModifyPlayers && (
          <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Inaktív játékosok mutatása
          </label>
        )}

        {/* Gyorsszűrők: darabszámmal, hogy látszódjon, hol van teendő */}
        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Gyorsszűrők">
          {QUICK_FILTERS.filter((f) => f.id === 'all' || quickFilterCounts[f.id] > 0 || quickFilter === f.id).map((f) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={quickFilter === f.id}
              onClick={() => setQuickFilter(quickFilter === f.id ? 'all' : f.id)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                quickFilter === f.id ? f.tone : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f.label} ({quickFilterCounts[f.id]})
            </button>
          ))}
        </div>
      </div>

      <div className="-mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
        <span>
          <span className="font-semibold text-gray-900">{filteredPlayers.length}</span> játékos
          {filteredPlayers.length !== players.length && <> (összesen {players.length})</>}
        </span>
        {isMobile && (
          <label className="flex items-center gap-2">
            <span className="text-gray-500">Rendezés:</span>
            <select
              value={`${sort.field}-${sort.dir}`}
              onChange={(e) => {
                const [field, dir] = e.target.value.split('-') as [SortField, 'asc' | 'desc'];
                setSort({ field, dir });
              }}
              className="rounded-md border-gray-300 py-1 text-sm"
            >
              <option value="name-asc">Név</option>
              <option value="ageGroup-asc">Korosztály</option>
              <option value="birth-asc">Születési dátum</option>
              <option value="medical-asc">Sportorvosi lejárat</option>
            </select>
          </label>
        )}
      </div>

      {/* Mobile Cards - csak az egyik nézet kerül a DOM-ba, adagokban */}
      {isMobile && (
      <div className="grid grid-cols-2 gap-3">
        {visiblePlayers.map((player) => {
          const badge = medicalBadge(player, medicalStateOf(player));
          return (
            <div
              key={player.id}
              onClick={() => handleViewPlayer(player)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleViewPlayer(player);
                }
              }}
              role="button"
              tabIndex={0}
              className={`bg-white rounded-lg shadow border p-3 flex flex-col cursor-pointer transition-colors hover:border-primary-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 ${player.isInactive ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between gap-1 mb-1.5">
                <div className="min-w-0">
                  <div className="font-medium text-sm text-gray-900 break-words hyphens-auto leading-snug">{player.name}</div>
                  {player.isInactive && (
                    <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-700 text-white">Inaktív</span>
                  )}
                  {(player.currentAbsence || player.medicalNotes) && (
                    <div className="mt-0.5 flex flex-wrap items-center gap-1">
                      <PlayerFlags player={player} />
                    </div>
                  )}
                  <div className="text-xs text-gray-500 truncate">
                    {player.ageGroups && player.ageGroups.length > 0 ? player.ageGroups.join(', ') : player.ageGroup}
                  </div>
                </div>
                {player.gender && (
                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${player.gender === 'Férfi' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'}`}>
                    {player.gender}
                  </span>
                )}
              </div>
              <div className="flex-1 space-y-0.5 text-xs text-gray-600">
                <div><span className="text-gray-400">Szül.:</span> {new Date(player.birth).toLocaleDateString('hu-HU')}</div>
                {player.ifaCode && <div className="truncate"><span className="text-gray-400">IFA:</span> {player.ifaCode}</div>}
                {player.taj && <div className="truncate"><span className="text-gray-400">TAJ:</span> {player.taj}</div>}
                <div>
                  <span className="text-gray-400">Sportorvosi: </span>
                  <span className={`whitespace-nowrap rounded px-1 font-medium ${badge.className}`}>{badge.text}</span>
                </div>
              </div>
              {canModifyPlayers && (
                <div
                  className="mt-2 flex justify-end gap-1 border-t border-gray-100 pt-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setIfaLinkPlayer(player)}
                    aria-label="Összekötés IFA-val"
                    title={player.ifaCode ? `IFA-kód: ${player.ifaCode}` : 'Összekötés IFA-val'}
                    className={`flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100 ${
                      player.ifaCode ? 'text-gray-400 hover:text-primary-600' : 'text-primary-600'
                    }`}
                  >
                    <LinkIcon className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleEditPlayer(player)} className="flex h-9 w-9 items-center justify-center text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-full">
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDeletePlayer(player)} className="flex h-9 w-9 items-center justify-center text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-full">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {hasMore && <LazyLoadTrigger key={visiblePlayers.length} onLoadMore={loadMore} className="col-span-2" />}
      </div>
      )}

      {/* Desktop Table */}
      {!isMobile && (
      <div className="bg-white rounded-lg shadow border">
        {/* Öt oszlop, tördelve - laptopon és tableten sem kell oldalra görgetni */}
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortHeader label="Név" field="name" sort={sort} onSort={toggleSort} />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortHeader label="Korosztály" field="ageGroup" sort={sort} onSort={toggleSort} />
              </th>
              <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortHeader label="Születés" field="birth" sort={sort} onSort={toggleSort} />
              </th>
              <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                TAJ / IFA
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <SortHeader label="Sportorvosi" field="medical" sort={sort} onSort={toggleSort} />
              </th>
              {canModifyPlayers && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <span className="sr-only">Műveletek</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {visiblePlayers.map((player) => {
              const badge = medicalBadge(player, medicalStateOf(player));
              const birth = new Date(player.birth).toLocaleDateString('hu-HU');
              return (
                <tr
                  key={player.id}
                  onClick={() => handleViewPlayer(player)}
                  className={`cursor-pointer hover:bg-gray-50 ${player.isInactive ? 'opacity-60' : ''}`}
                >
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <div className="flex items-center gap-3">
                      <div className="hidden sm:flex w-8 h-8 shrink-0 bg-primary-100 rounded-full items-center justify-center">
                        <UserIcon className="h-4 w-4 text-primary-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="font-medium">{player.name}</span>
                          {player.isInactive && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-700 text-white">Inaktív</span>
                          )}
                          <PlayerFlags player={player} />
                        </div>
                        {/* Keskenyebb képernyőn a rejtett oszlopok adatai a név alatt */}
                        <div className="mt-0.5 text-xs text-gray-500">
                          {player.gender && <span>{player.gender}</span>}
                          <span className="lg:hidden">
                            {player.gender && ' · '}
                            {birth}
                          </span>
                          {(player.taj || player.ifaCode) && (
                            <span className="xl:hidden">
                              {player.ifaCode && ` · IFA ${player.ifaCode}`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {player.ageGroups && player.ageGroups.length > 0 ? player.ageGroups.join(', ') : player.ageGroup}
                  </td>
                  <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-700">
                    <div className="whitespace-nowrap">{birth}</div>
                    {player.placeOfBirth && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                        <span className="truncate max-w-[10rem]">{player.placeOfBirth}</span>
                      </div>
                    )}
                  </td>
                  <td className="hidden xl:table-cell px-4 py-3 text-xs text-gray-700">
                    {player.taj || player.ifaCode ? (
                      <div className="space-y-0.5">
                        {player.taj && (
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <IdentificationIcon className="h-3.5 w-3.5 text-gray-400" />
                            <span className="text-gray-400">TAJ</span> {player.taj}
                          </div>
                        )}
                        {player.ifaCode && (
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <IdentificationIcon className="h-3.5 w-3.5 text-gray-400" />
                            <span className="text-gray-400">IFA</span> {player.ifaCode}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center whitespace-nowrap px-2 py-1 rounded-full text-xs font-medium ${badge.className}`}>
                      {badge.text}
                    </span>
                  </td>
                  {canModifyPlayers && (
                    <td
                      className="px-2 py-3 text-sm text-gray-700 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-end gap-0.5">
                        <button
                          onClick={() => setIfaLinkPlayer(player)}
                          aria-label="Összekötés IFA-val"
                          title={player.ifaCode ? `IFA-kód: ${player.ifaCode}` : 'Összekötés IFA-val'}
                          className={`p-1.5 rounded-full hover:bg-gray-100 ${
                            player.ifaCode ? 'text-gray-400 hover:text-primary-600' : 'text-primary-600'
                          }`}
                        >
                          <LinkIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditPlayer(player)}
                          aria-label="Szerkesztés"
                          title="Szerkesztés"
                          className="p-1.5 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-full"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePlayer(player)}
                          aria-label="Törlés"
                          title="Törlés"
                          className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-full"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {hasMore && (
          <>
            <LazyLoadTrigger key={visiblePlayers.length} onLoadMore={loadMore} />
            <button
              type="button"
              onClick={loadMore}
              className="w-full border-t border-gray-100 py-3 text-sm font-medium text-primary-700 hover:bg-gray-50"
            >
              További {Math.min(remaining, PAGE_SIZE)} betöltése ({remaining} van még)
            </button>
          </>
        )}
      </div>
      )}

      {filteredPlayers.length === 0 && (
        <div className="text-center py-12">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nincsenek játékosok</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || selectedAgeGroup || quickFilter !== 'all'
              ? 'A keresési feltételeknek megfelelő játékos nem található.'
              : 'Kezdj el új játékosok hozzáadásával.'
            }
          </p>
        </div>
      )}

      {/* Modals */}
      <IfaLinkModal
        isOpen={ifaLinkPlayer !== null}
        onClose={() => setIfaLinkPlayer(null)}
        player={ifaLinkPlayer}
        onLinked={async () => setPlayers(await fetchPlayers())}
      />

      <PlayerDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedPlayer(null);
          navigate('/players', { replace: true });
        }}
        player={selectedPlayer}
        onEdit={canModifyPlayers && selectedPlayer ? () => handleEditPlayer(selectedPlayer) : undefined}
        onToggleActive={canModifyPlayers ? handleToggleActive : undefined}
        onAbsenceChanged={handleAbsenceChanged}
      />

      <PlayerImportModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImported={loadPlayers}
      />

      <PlayerModal
        isOpen={isPlayerModalOpen}
        onClose={() => {
          setIsPlayerModalOpen(false);
          setSelectedPlayer(null);
        }}
        player={selectedPlayer}
        onSave={handlePlayerSaved}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedPlayer(null);
        }}
        onConfirm={handlePlayerDeleted}
        title="Játékos törlése"
        message={`Biztosan törölni szeretnéd a következő játékost: ${selectedPlayer?.name}?`}
        itemName={selectedPlayer?.name || ''}
      />

      <BulkInviteModal
        isOpen={isBulkInviteOpen}
        onClose={() => setIsBulkInviteOpen(false)}
        players={players}
      />

      {/* Sportorvosi ellenőrzés: a jelenleg szűrt lista aktív játékosaira */}
      <SportDoctorCheckModal
        isOpen={isMedicalModalOpen}
        onClose={() => setIsMedicalModalOpen(false)}
        players={baseFiltered.filter((p) => !p.isInactive)}
        canModify={canModifyPlayers}
        onChanged={async () => setPlayers(await fetchPlayers())}
      />
    </div>
  );
};

export default PlayersList;